// The graveyard (docs/164 "The graveyard's look", docs/166 C2, `grave_board.py.txt`): the churchyard's own
// quieter grass with gravel only on its paths, the grass's fray lying over the gravel at half depth so a
// one-square path stays a path, and a wall no open square touches gone back to the country: its `rock`
// file as ground with a yew on one in three.
import { EDGE_DEPTHS } from '../../../ui/screens/map/groundEdges';
import type { Standing } from '../types';
import type { Canvas, Turn } from './canvas';
import { C, type Ctx } from './context';
import type { Ground } from './ground';
import { trailSquares } from './trail';

export const isGraveyard = (ctx: Ctx): boolean => ctx.set.kit === 'graveyard' && ctx.has('floor_grass');

const paths = new WeakMap<Ctx, Set<number>>();
/** Where the gravel goes: the trail between the ways in and out, every gate, and the squares in front of a gate in a wall. */
function pathOf(ctx: Ctx): Set<number> {
  let p = paths.get(ctx);
  if (p) return p;
  const { sq } = ctx, { w, h } = sq;
  p = new Set(trailSquares(ctx));
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const s = sq.at(x, y);
    if (s !== 'door' && s !== 'way') continue;
    p.add(y * w + x);
    for (const [dx, dy] of [[0, 1], [0, -1], [1, 0], [-1, 0]] as const) {
      const n = sq.at(x + dx, y + dy);
      if (n === 'floor' || n === 'rough') p.add((y + dy) * w + x + dx);
    }
  }
  paths.set(ctx, p);
  return p;
}

/** Turns for the grass: a quarter turn too, except where the art has an up (the highland's heather, the jungle's ferns). */
function turnOf(ctx: Ctx, r: number): Turn | undefined {
  const upright = ctx.set.biome === 'highland' || ctx.set.biome === 'jungle';
  const turns: Array<Turn | undefined> = upright ? [undefined, { flipX: true }] : [undefined, { flipX: true }, { rot90: true }];
  return turns[r % turns.length];
}

function lay(ctx: Ctx, name: string, piece: string, x: number, y: number): Ground | undefined {
  const vs = ctx.all(piece);
  if (!vs.length) return undefined;
  const r = ctx.roll(`g:${piece}`, x, y), p = vs[r % vs.length]!, t = Math.floor(r / vs.length);
  const turn = piece === 'floor' ? (t % 2 ? { flipX: true } : undefined) : turnOf(ctx, t);
  return { name, piece: p, ...(turn ? { turn } : {}), key: `${p.frame}/${t}` };
}

export function graveGroundOf(ctx: Ctx, x: number, y: number, s: string, g: Ground | undefined): Ground | undefined {
  if (s !== 'floor' && s !== 'rough' && s !== 'door' && s !== 'way') return g;
  if (g && g.name !== 'floor' && g.name !== 'floor_patch') return g;
  return pathOf(ctx).has(y * ctx.sq.w + x) ? lay(ctx, 'floor', 'floor', x, y) ?? g : lay(ctx, 'grass', 'floor_grass', x, y) ?? g;
}

/** The grass's fray over the gravel at half depth, and the `rock` file on the walls gone back to the country. */
export function graveAfterGround(ctx: Ctx, out: Canvas, grounds: ReadonlyArray<Ground | undefined>): void {
  const { sq } = ctx, { w, h } = sq;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (sq.at(x, y) !== 'rock') continue;
    const g = lay(ctx, 'rock', 'rock', x, y) ?? lay(ctx, 'grass', 'floor_grass', x, y);
    if (g) out.draw(g.piece, x * C, y * C, g.turn, true);
  }
  const SIDES = [['n', 0, -1], ['s', 0, 1], ['w', -1, 0], ['e', 1, 0]] as const;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (grounds[y * w + x]?.name !== 'floor' || grounds[y * w + x]?.piece.piece !== 'floor') continue;
    for (const [s, dx, dy] of SIDES) {
      const n = grounds[(y + dy) * w + x + dx];
      if (!sq.on(x + dx, y + dy) || n?.name !== 'grass') continue;
      const prof = EDGE_DEPTHS[ctx.roll(`gf:${s}`, x, y) % EDGE_DEPTHS.length]!;
      for (let i = 0; i < C; i++) for (let k = 0; k < Math.floor(prof[i]! / 2) + 1; k++) {
        const [ix, iy] = s === 'n' ? [i, k] : s === 's' ? [i, C - 1 - k] : s === 'w' ? [k, i] : [C - 1 - k, i];
        out.copy(x * C + ix, y * C + iy, n.piece, ix, iy, n.turn);
      }
    }
  }
}

/** A yew on one in three of the squares gone back to the country. */
export function graveStanding(ctx: Ctx, out: Canvas, standing: Standing[]): void {
  const { sq } = ctx, { w, h } = sq;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (sq.at(x, y) !== 'rock' || ctx.roll('rk', x, y) % 3) continue;
    const p = ctx.pick('stand_yew', 'rs', x, y);
    if (!p) continue;
    const px = x * C + C / 2 - Math.floor(p.w / 2), py = (y + 1) * C - p.h, cx = px + p.w / 2, cy = py + p.h - 2;
    out.ellipse(cx - 9, cy - 3, cx + 9, cy + 3, [10, 20, 10, 80]);
    standing.push({ kind: 'grove', at: { x, y }, x: px, y: py, w: p.w, h: p.h, foot: y * C + C / 2, frame: p.frame });
  }
}
